import { useEffect, useImperativeHandle, useRef } from 'react';
import { Button, ControlLabel, Dialog, DialogContent, DialogRef, Radio, Stack } from 'src/app/components';
import { HookFormProvider, InputDatePicker, InputTextField, InputTextEditor, InputImageUpload, InputRadioGroup } from 'src/app/components/HookForm';
import InputSelectMall from 'src/app/components/HookForm/InputSelect/InputSelect.mall';
import { useAddPrizeForm, defaultPrizeForm } from './AddPrizePopup.hooks';
import { DateObject } from 'react-multi-date-picker';
import dayjs from 'dayjs';
import { PrizeListModel } from '../../../models/LuckyDraw.model';
import { InfoPrimaryIcon } from 'src/app/components/Icons';
import { getErrorMessage } from 'src/app/utils/api-utils';
interface AddPrizePopupProps {
  dialogRef: React.RefObject<DialogRef>;
  save?: () => void;
  cancel?: () => void;
  refetch?: () => void;
  id?: string; // <-- example of an additional prop you expect
  [key: string]: any; // <-- allows any other props (optional)
}

const AddPrizePopup = ({ dialogRef, save, cancel, refetch, ...props }: AddPrizePopupProps) => {
  const { form, setIsEdit, isEdit, enabledFields, submit } = useAddPrizeForm(props);
  const isPrintable = props.currentDetail?.is_printable === true || (props.currentDetail?.is_printable as unknown) === 'true';
  const printDateType = props.currentDetail?.print_date_type;
  const isMaxDayToPrint = isPrintable && printDateType === 'MAX_DAY_TO_PRINT';
  const isFixDate = (isPrintable && printDateType === 'FIX_DATE') || (!isMaxDayToPrint && Boolean(props.currentDetail?.last_print_date));
  const campaignLastPrintDate = props.currentDetail?.last_print_date;

  const prizeType = form.watch('type');
  const isConsolation = prizeType === 'CONSOLATION';
  const isGrandPrize = !prizeType || prizeType === 'GRAND_PRIZE';

  const showLastPrintDate = isPrintable && isFixDate;

  const selectedDirectory = form.watch('directory');
  const selectedMallId = typeof selectedDirectory === 'string'
    ? selectedDirectory
    : (selectedDirectory?.id || (selectedDirectory as any)?.value || form.watch('draw_directory_id'));
  const prizeList: PrizeListModel[] = props.prizeList || [];

  const existingConsolationPrize = prizeList.find((p: PrizeListModel) => {
    if (props.itemToEdit?.id && p.id === props.itemToEdit.id) {
      return false;
    }
    const pMallId = p.draw_directory_id || p.draw_directory?.id || p.directory?.id;
    return p.type === 'CONSOLATION' && pMallId && selectedMallId && String(pMallId) === String(selectedMallId);
  });

  const showConsolationNotice = isConsolation && Boolean(selectedMallId) && Boolean(existingConsolationPrize);
  const existingConsolationDrawDate = existingConsolationPrize?.draw_date
    ? dayjs(existingConsolationPrize.draw_date).format('DD/MM/YYYY')
    : '';

  const lastSyncedMallRef = useRef<string | null>(null);
  const userGrandPrizeDateRef = useRef<string>('');

  useEffect(() => {
    if (!props.itemToEdit?.id && isConsolation && existingConsolationPrize?.draw_date && selectedMallId !== lastSyncedMallRef.current) {
      lastSyncedMallRef.current = selectedMallId;
      if (form.getValues('draw_date') !== existingConsolationPrize.draw_date) {
        form.setValue('draw_date', existingConsolationPrize.draw_date);
      }
    }
  }, [isConsolation, existingConsolationPrize?.draw_date, selectedMallId, props.itemToEdit?.id, form]);

  useEffect(() => {
    if (!isPrintable) return;
    if (isConsolation) {
      if (isFixDate && campaignLastPrintDate) {
        const formattedCampaignDate = dayjs(campaignLastPrintDate).format('YYYY-MM-DD');
        if (form.getValues('last_print_date') !== formattedCampaignDate) {
          form.setValue('last_print_date', formattedCampaignDate, { shouldValidate: true });
        }
      } else if (!isFixDate && form.getValues('last_print_date')) {
        form.setValue('last_print_date', '');
      }
    } else if (isGrandPrize) {
      if (isFixDate) {
        const currentVal = form.getValues('last_print_date');
        const formattedCampaignDate = campaignLastPrintDate ? dayjs(campaignLastPrintDate).format('YYYY-MM-DD') : null;
        if (currentVal && currentVal === formattedCampaignDate && userGrandPrizeDateRef.current !== formattedCampaignDate) {
          form.setValue('last_print_date', userGrandPrizeDateRef.current || '', { shouldValidate: true });
        }
      } else if (isMaxDayToPrint) {
        if (form.getValues('last_print_date')) {
          form.setValue('last_print_date', '');
        }
      }
    }
    if (form.getValues('draw_date')) {
      form.trigger('draw_date');
    }
  }, [isPrintable, isFixDate, isMaxDayToPrint, isConsolation, isGrandPrize, campaignLastPrintDate, form]);

  const submitButtonClick = () => {
    const item = props.itemToEdit;
    if (item?.id) {
      const current = form.getValues();
      if (item.type) {
        form.setValue('type', current.type || item.type, { shouldValidate: true });
      }
      if (item.name) {
        form.setValue('name', current.name || item.name);
      }
      if (item.directory) {
        form.setValue('directory', current.directory?.id ? current.directory : item.directory);
      }
    }
    return form.handleSubmit(async ({image, ...formData}) => {
    const type = formData.type || item?.type;
    const name = formData.name || item?.name;
    const directory = formData.directory?.id ? formData.directory : item?.directory;
    const payload: PrizeListModel = {
      ...formData,
      type,
      prize_type: type,
      name,
      directory,
      draw_directory_id: directory?.id || formData.draw_directory_id || item?.directory?.id,
    }
    if (!payload.id) {
      delete payload.id;
    }
    if (typeof image === 'string' && image.includes('base64,')) {
      payload.image = image.split('base64,', 2)[1];
    } else {
      delete payload.image;
    }
    if (isPrintable && isFixDate) {
      if (type === 'CONSOLATION') {
        const consolationDate = campaignLastPrintDate || formData.last_print_date;
        if (consolationDate) {
          payload.last_print_date = dayjs(consolationDate).format('YYYY-MM-DD');
        } else {
          delete payload.last_print_date;
        }
      } else if (formData.last_print_date) {
        payload.last_print_date = dayjs(formData.last_print_date).format('YYYY-MM-DD');
      } else {
        delete payload.last_print_date;
      }
    } else {
      delete payload.last_print_date;
    }
    hitPostAPI(payload);
  })();
  }

  const hitPostAPI = async (payload: PrizeListModel) => {
    try {
      await submit.mutateAsync(payload)
      refetch && refetch()
      clickCancel()
    } catch (error: any) {
      errorPostApi(error)
    }
  }

  const scrollToTop = () => {
    const modalWrapper = document.getElementById('dialog-wrapper-content');
    if (modalWrapper) {
      const modalBody = modalWrapper.querySelector('.overflow-y-scroll');
      if (modalBody) {
        modalBody.scrollTo({
          top: modalWrapper.offsetTop,
          behavior: 'smooth',
        });
      }
    }
  };

  const errorPostApi = (err?: any) => {
    const fieldErrors =
      err?.response?.data?.response_output?.errors ||
      err?.response?.data?.errors ||
      err?.response?.data?.response_output?.detail?.errors;

    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      const fieldMapper: Record<string, string> = {
        draw_directory_id: 'directory',
        directory_id: 'directory',
      };
      fieldErrors.forEach((item: any) => {
        if (item.field) {
          const targetField = fieldMapper[item.field] || item.field;
          const message =
            item.message?.en ||
            item.message?.id ||
            (typeof item.message === 'string' ? item.message : 'Invalid field');
          form.setError(targetField, {
            type: 'serverError',
            message,
          });
        }
      });
      scrollToTop();
      return;
    }

    if (err?.response?.data?.response_schema?.response_code?.includes('400')) {
      const serverMsg = getErrorMessage(err, true)?.en;
      form.setError('name', {
        type: 'required',
        message: serverMsg || 'Prizes name already exist in this mall, please enter another name or change draw location.',
      });
      scrollToTop();
    }
  };

  const localRef = useRef<DialogRef>(null);

  function clickCancel() {
    lastSyncedMallRef.current = null;
    userGrandPrizeDateRef.current = '';
    form.reset();
    localRef.current?.hide();
    cancel && cancel();
    setIsEdit(false)
  }

  useEffect(() => {
    lastSyncedMallRef.current = null;
    if (props.itemToEdit?.id) {
      setIsEdit(true);
      const item = props.itemToEdit;
      const isItemConsolation = item.type === 'CONSOLATION';
      const grandPrizeDate = !isItemConsolation && !isMaxDayToPrint && item.last_print_date ? item.last_print_date : '';
      userGrandPrizeDateRef.current = grandPrizeDate;

      const initialLastPrintDate = isPrintable && isFixDate
        ? (isItemConsolation && campaignLastPrintDate ? campaignLastPrintDate : (item.last_print_date || ''))
        : '';

      form.reset({
        ...defaultPrizeForm,
        ...item,
        type: item.type || '',
        name: item.name || '',
        directory: item.directory || {},
        last_print_date: initialLastPrintDate ? dayjs(initialLastPrintDate).format('YYYY-MM-DD') : '',
      });
    } else {
      setIsEdit(false);
      userGrandPrizeDateRef.current = '';
      form.reset(defaultPrizeForm);
    }
  }, [props.itemToEdit, form, setIsEdit, isFixDate, isMaxDayToPrint, campaignLastPrintDate, isPrintable]);


  useImperativeHandle(dialogRef, () => ({
    show: () => localRef.current?.show(),
    hide: () => localRef.current?.hide(),
  }), []);

  return (
    <Dialog
      closeOnBackdropClicked={false}
      title={(isEdit? 'Edit' : 'Add New')+" Prize"}
      size="md"
      ref={localRef}
      onHide={clickCancel}
    >
      <DialogContent className="bg-white flex flex-col">
        <>
          <HookFormProvider
            enables={enabledFields()}
            form={form}
          >
            <Stack className="p-6">
              <ControlLabel label="Draw Location">
                <InputSelectMall placeholder="Select draw location" isMulti={false} form={form} rule={{ required: true }} />
              </ControlLabel>
              <ControlLabel label="Draw Date" required={true}>
                <div className="flex flex-col gap-3">
                  <InputDatePicker
                    placeholder="Select draw date"
                    rule={{
                      required: true,
                      validate: (val: string) => {
                        if (!val) return 'Field cannot be empty';
                        if (showLastPrintDate) {
                          const effectiveLastPrintDate = isConsolation
                            ? (campaignLastPrintDate || form.getValues('last_print_date'))
                            : form.getValues('last_print_date');
                          if (effectiveLastPrintDate && dayjs(val).startOf('day').isBefore(dayjs(effectiveLastPrintDate).startOf('day'))) {
                            return 'Draw Date cannot be earlier than Last Print Date';
                          }
                        }
                        return true;
                      },
                    }}
                    name="draw_date"
                    minDate={props.currentDetail?.end_date ? new DateObject(props.currentDetail.end_date).add(1, 'day') : undefined}
                    form={form}
                  />
                  {showConsolationNotice && (
                    <div className="flex items-start gap-3 p-4 border border-neutral-200 rounded-lg bg-neutral-50/50">
                      <InfoPrimaryIcon className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-gray-500 leading-relaxed">
                        You have added a Consolation Prize to that Draw location, and the draw date is {existingConsolationDrawDate}. If you select a different date for the next Consolation Prize, it will change the draw date for all previously added Consolation Prizes.
                      </p>
                    </div>
                  )}
                </div>
              </ControlLabel>
              <ControlLabel label="Prizes Type" required={true}>
                <InputRadioGroup form={form} name="type" rule={{ required: true }}>
                  <Radio key={'grand_prize'} label="Grand Prize" value="GRAND_PRIZE" required />
                  <Radio key={'consolation'} label="Consolation" value="CONSOLATION" required />
                </InputRadioGroup>
              </ControlLabel>
              <ControlLabel label="Prizes Name">
                <InputTextField
                  placeholder="Enter prizes name"
                  rule={{ required: true }}
                  name="name"
                  maxLength={120}
                />
              </ControlLabel>
              {showLastPrintDate && (
                <ControlLabel label="Last Print Date" required={true}>
                  <InputDatePicker
                    placeholder="Select last print date"
                    name="last_print_date"
                    form={form}
                    minDate={
                      props.itemToEdit?.last_print_date && dayjs(props.itemToEdit.last_print_date).isBefore(dayjs().startOf('day'))
                        ? new DateObject(props.itemToEdit.last_print_date)
                        : new DateObject()
                    }
                    maxDate={new DateObject(dayjs().add(1, 'month').endOf('month').toDate())}
                    disabled={isConsolation}
                    onChange={(val) => {
                      if (isGrandPrize && typeof val === 'string') {
                        userGrandPrizeDateRef.current = val;
                      }
                      if (form.getValues('draw_date')) {
                        form.trigger('draw_date');
                      }
                    }}
                    rule={{
                      required: isFixDate && isGrandPrize,
                      validate: (val: string) => {
                        if (!isFixDate) return true
                        if (isConsolation) return true
                        if (!val) return 'Field cannot be empty'
                        const existingLastPrintDate = props.itemToEdit?.last_print_date
                        if (existingLastPrintDate && dayjs(val).isSame(dayjs(existingLastPrintDate), 'day')) {
                          return true
                        }
                        const selected = dayjs(val)
                        const today = dayjs().startOf('day')
                        if (selected.isBefore(today)) {
                          return 'Cannot be selected as a past date'
                        }
                        const maxAllowed = dayjs().add(1, 'month').endOf('month')
                        if (selected.isAfter(maxAllowed)) {
                          return 'Date cannot be after the end of the following month'
                        }
                        return true
                      },
                    }}
                  />
                </ControlLabel>
              )}

              <ControlLabel label="Description (EN)">
                <div>
                  <InputTextEditor height={150} form={form} name="description_eng" />
                </div>
              </ControlLabel>
              <ControlLabel label="Description (ID)">
                <div>
                  <InputTextEditor height={150} form={form} name="description_idn" />
                </div>
              </ControlLabel>
              {isGrandPrize && (
                <ControlLabel label="Image" required={true}>
                  <div className="grid grid-cols-2 gap-8">
                    <InputImageUpload
                      placeholder="Upload Photo (JPG/PNG)"
                      maxSize={Math.pow(1024, 2) * 5}
                      accept={['.jpg', '.jpeg', '.png']}
                      rule={{ required: true }}
                      form={form}
                      name="image"
                    />
                    <div className="flex flex-col justify-center">
                      <ul className="text-xs text-gray-500 list-disc list-inside dark:text-gray-400">
                        <li className="mb-4">
                          Size: Max size of file is <strong>5 MB</strong>.
                        </li>
                        <li className="mb-4">
                          Photo ratio is <strong>16/9</strong>
                        </li>
                        <li>
                          File format: only allow <strong>.jpg / .png</strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </ControlLabel>
              )}
              <ControlLabel label="Prizes Qty" required={true}>
                <InputTextField
                  placeholder="Enter prizes quantity"
                  name="quantity"
                  type="number"
                  rule={{
                    required: true,
                    min: {
                      value: 1,
                      message: 'Minimal qty is 1',
                    },
                  }}
                />
              </ControlLabel>

            </Stack>
            <Stack
              className={'border-t pt-4 border-neutral-200'}
              direction="row"
              gap={4}
            >
              <Button variant='outlined' onClick={clickCancel}>Cancel</Button>
              <Stack
                direction="row"
                justify='end'
                gap={4}
              >
                <Button onClick={() => submitButtonClick()} loading={submit.isLoading}>{isEdit? 'Save':'Add'}</Button>
              </Stack>
            </Stack>
          </HookFormProvider>
        </>
      </DialogContent>
    </Dialog>
  );
};

export default AddPrizePopup;
